/**
 * The approval dialog's second step (ADR-034): straight after a workspace
 * approves an app that declares roles, an owner turns them on — the same
 * panel as on the app's Access screen, which is where "Later" leaves it.
 */
import { Link } from 'react-router-dom';
import { useAppAccessScreen, type AppAccessScope } from '../hooks/useAppAccess';
import { useWorkspaceMembers } from '../hooks/useWorkspaceTeam';
import { SetupRolesPanel } from '../pages/app-access/SetupRolesPanel';
import { MembersLoadError } from '../pages/app-access/members';
import { appAccessSectionPath, describeAppAccessError } from '../pages/app-access/shared';

export function ApprovalRoleSetup({
  scope,
  appName,
  onDone,
}: {
  scope: AppAccessScope;
  appName: string;
  onDone: () => void;
}) {
  const screen = useAppAccessScreen(scope);
  const members = useWorkspaceMembers(scope.holderId);
  if (screen.isLoading || members.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (screen.isError || !screen.data) {
    return (
      <p className="text-sm text-destructive">
        {describeAppAccessError(screen.error)}{' '}
        <Link
          className="underline"
          to={appAccessSectionPath(scope.holderId, scope.archeId, 'people')}
        >
          Open its Access screen
        </Link>
      </p>
    );
  }
  if (screen.data.access.activation?.mode === 'activating') {
    return (
      <p className="text-sm text-muted-foreground">
        Roles are on here, but setup is not finished.{' '}
        <Link
          className="underline"
          to={appAccessSectionPath(scope.holderId, scope.archeId, 'people')}
        >
          Finish on the Access screen
        </Link>
      </p>
    );
  }
  if (screen.data.access.activated) {
    return <p className="text-sm text-muted-foreground">Roles are already on here.</p>;
  }
  // Setting roles up for an empty list would turn them on for nobody.
  if (members.isError) return <MembersLoadError onRetry={() => void members.refetch()} />;
  return (
    <SetupRolesPanel
      scope={scope}
      appName={appName}
      access={screen.data.access}
      members={members.data ?? []}
      records={screen.data.records ?? []}
      onActivated={onDone}
    />
  );
}
